'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect } from 'react';

import styles from './blog-category-search.module.css';

interface CategoryItem {
  title: string;
  value: string;
}

interface BlogCategorySearchProps {
  categories?: CategoryItem[];
  title?: string;
  className?: string;
  isConsumerNews?: boolean;
}

export function BlogCategorySearch({
  categories = [
    { title: 'View all', value: '' },
    { title: 'Health insurance', value: 'Health insurance' },
    { title: 'Medicare', value: 'Medicare' },
    { title: 'Blogs', value: 'Blogs' },
  ],
  title = 'Browse by Category',
  className = '',
  isConsumerNews = false,
}: BlogCategorySearchProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const currentCategory = searchParams.get('category') || '';
  const basePath = isConsumerNews ? '/consumer-news' : '/partner-news';

  // Scroll to #tagged when on category page and category is selected
  useEffect(() => {
    if (
      currentCategory &&
      window.location.pathname === `${basePath}/category`
    ) {
      // Small delay to ensure the page has loaded
      const timer = setTimeout(() => {
        const taggedElement = document.getElementById('tagged');
        if (taggedElement) {
          // Get the element's position and account for header height
          const elementRect = taggedElement.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          const headerOffset = 100; // Adjust this value based on your header height

          window.scrollTo({
            top: absoluteElementTop - headerOffset,
            behavior: 'instant',
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [currentCategory]);

  const handleCategoryClick = (categoryValue: string) => {
    if (!categoryValue || categoryValue.trim() === '') {
      // Navigate to main blog page for "View all"
      router.push(basePath);
    } else {
      // Navigate to category results with category filter
      const encodedCategory = encodeURIComponent(categoryValue.trim());
      router.push(`${basePath}/category?category=${encodedCategory}`);
    }
  };

  return (
    <div className={`${styles.categoryContainer} ${className}`}>
      {title && <h3 className={styles.categoryTitle}>{title}</h3>}
      <div className={styles.categoryList}>
        {categories.map((category, index) => {
          const isActive =
            (category.value === '' && currentCategory === '') ||
            category.value === currentCategory;

          return (
            <button
              key={index}
              onClick={() => handleCategoryClick(category.value)}
              className={`${styles.categoryButton} ${isActive ? styles.categoryButtonActive : ''}`}
              type="button"
            >
              {category.title}
            </button>
          );
        })}
      </div>
    </div>
  );
}
